import { useState, useEffect, useMemo, useRef, useCallback } from 'react';

import { Stack } from '@mui/material';

import {
  Project,
  ActionItem,
  Unit,
  LeaseLifecycle,
  Process,
} from '@urbanmix-tech/shared-js';

import { convertPseudoDateToRealDate } from '@/helpers/common';
import { getDatePeriod } from '@/constants/datePeriods';

import { ProjectCard } from './ProjectCard';

export function getStats(units: Unit[]): Record<
  LeaseLifecycle,
  {
    units: number;
    percentage: number;
  }
> {
  const stats: Record<string, number> = {};

  Object.values(LeaseLifecycle).forEach((v) => {
    const cnt = units.filter((unit) =>
      Array.isArray(unit.currentState)
        ? unit.currentState.length > 0
          ? unit.currentState.includes(v)
          : LeaseLifecycle.NOTICE === v
        : ((unit.currentState as LeaseLifecycle) || LeaseLifecycle.NOTICE) === v
    ).length;

    stats[v] = cnt;
  });

  return Object.fromEntries(
    Object.entries(stats).map(([key, value]) => {
      return [
        key,
        {
          units: value,
          percentage: units.length > 0 ? value / units.length : 0,
        },
      ];
    })
  ) as Record<LeaseLifecycle, { units: number; percentage: number }>;
}

const CARD_MIN_WIDTH = 318;
const CARD_MIN_HEIGHT = 380;
const CARD_ASPECT_RATIO = 1.5; // height/width ratio
const PADDING = 16;
const ITEMS_PER_PAGE = 20;

export type ProjectCardListProps = {
  projects: Project[];
  unitsByProjectId: Record<string, Unit[]>;
  processesByProjectId?: Record<string, Process[]>;
  actionItemsByProjectId?: Record<string, ActionItem[]>;
  statusFilter: string[];
  processStatusFilter?: string[];
  processTypeFilter?: string[];
  unitProcessFilter?: string[];
  datePeriodFilter?: string[];
  viewType?: 'lease' | 'process' | 'date';
};

export function ProjectCardList({
  projects,
  unitsByProjectId,
  processesByProjectId,
  actionItemsByProjectId = {},
  statusFilter,
  processStatusFilter = [],
  processTypeFilter = [],
  unitProcessFilter = [],
  datePeriodFilter = [],
  viewType = 'lease',
}: ProjectCardListProps) {
  // Action items are provided (already reconstructed from the compact overview
  // projection) instead of fetching the entire action_items collection here.
  const actionsByProjectId = actionItemsByProjectId;

  // Filter projects based on process or date period filters
  const filteredProjects = useMemo(() => {
    if (
      processStatusFilter.length === 0 &&
      processTypeFilter.length === 0 &&
      unitProcessFilter.length === 0 &&
      datePeriodFilter.length === 0
    ) {
      return projects;
    }

    return projects.filter((project) => {
      const projectProcesses = processesByProjectId?.[project.id] || [];
      const projectUnits = unitsByProjectId[project.id] || [];
      const projectActionItems = actionsByProjectId[project.id] || [];

      // Process status filter
      if (processStatusFilter.length > 0) {
        const hasMatchingStatus = projectProcesses.some((process) =>
          processStatusFilter.includes(process.status)
        );
        if (!hasMatchingStatus) return false;
      }

      // Process type filter
      if (processTypeFilter.length > 0) {
        const hasMatchingType = projectProcesses.some((process) =>
          process.type && processTypeFilter.includes(process.type)
        );
        if (!hasMatchingType) return false;
      }

      // Unit process filter (active vs idle)
      if (unitProcessFilter.length > 0) {
        const activeUnitIds = new Set(
          projectProcesses
            .filter((p) => p.status === 'start')
            .map((p) => p.unitId)
        );

        const hasActive = unitProcessFilter.includes('active');
        const hasIdle = unitProcessFilter.includes('idle');

        if (hasActive && activeUnitIds.size === 0) return false;
        if (hasIdle && activeUnitIds.size === projectUnits.length) return false;
      }

      // Date period filter
      if (datePeriodFilter.length > 0) {
        const hasMatchingPeriod = projectActionItems.some((task) => {
          if (task.status === 'done') return false;

          // Handle unscheduled filter
          if (datePeriodFilter.includes('unscheduled')) {
            if (!task.dueDate) return true;
          }

          if (task.dueDate) {
            const date = convertPseudoDateToRealDate(task.dueDate);
            if (date) {
              const period = getDatePeriod(date);
              return datePeriodFilter.includes(period);
            }
          }

          return false;
        });

        if (!hasMatchingPeriod) return false;
      }

      return true;
    });
  }, [
    projects,
    processesByProjectId,
    unitsByProjectId,
    actionsByProjectId,
    processStatusFilter,
    processTypeFilter,
    unitProcessFilter,
    datePeriodFilter,
  ]);

  return (
    <>
      <div className="w-full h-full overflow-hidden flex flex-col">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-16 p-2 w-full overflow-y-auto">
          {filteredProjects.map((project) => (
            <ProjectCard
              hasActiveAction={false}
              key={project.id}
              projectId={project.id}
              buildingId={project.buildings[0].id}
              name={project.name}
              address={project.address}
              building={project.buildings[0]}
              units={unitsByProjectId[project.id]}
              processes={processesByProjectId?.[project.id]}
              actionItems={actionsByProjectId[project.id]}
              stats={getStats(unitsByProjectId[project.id])}
              width={0} // Passed as 0 or undefined, ProjectCard should handle flexibility
              height={undefined} // Let card grow naturally
              statusFilter={statusFilter}
              processStatusFilter={processStatusFilter}
              processTypeFilter={processTypeFilter}
              unitProcessFilter={unitProcessFilter}
              datePeriodFilter={datePeriodFilter}
              viewType={viewType}
            />
          ))}
        </div>
      </div>
    </>
  );
}
